import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  AlertTriangle, Activity, Pill, FileText, CheckCircle, Shield,
  Users, Clock, DollarSign, Clipboard, BedDouble, Heart,
  ChevronRight, AlertCircle, Zap, Package, Phone, UserCheck,
  Home, Stethoscope, ClipboardList, TrendingDown, Bell
} from "lucide-react";

const FallAutomationPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('incident');
  const [loading, setLoading] = useState(false);
  const [automationResult, setAutomationResult] = useState(null);
  const [beersResult, setBeersResult] = useState(null);
  const [dmeRecommendations, setDmeRecommendations] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [showDMEModal, setShowDMEModal] = useState(false);
  const [selectedDME, setSelectedDME] = useState([]);
  
  // Incident form state
  const [incidentForm, setIncidentForm] = useState({
    patient_id: '',
    patient_name: '',
    chart_id: '',
    fall_date: new Date().toISOString().split('T')[0],
    fall_time: '',
    location: 'bedroom',
    witnessed: false,
    witness_name: '',
    description: '',
    injury_present: false,
    severity: 'unknown',
    injury_description: '',
    vital_signs_checked: false,
    neuro_check_completed: false,
    md_notified: false,
    family_notified: false,
    documented_by: user?.name || 'Staff'
  });
  
  // Medications for Beers check
  const [medications, setMedications] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadDMERecommendations();
    loadInterventions();
  }, []);

  const loadDMERecommendations = async () => {
    try {
      const response = await api.get('/ehr/fall-automation/dme/recommendations');
      setDmeRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error("Failed to load DME recommendations:", error);
    }
  };

  const loadInterventions = async () => {
    try {
      const response = await api.get('/ehr/fall-automation/interventions');
      setInterventions(response.data.interventions || []);
    } catch (error) {
      console.error("Failed to load interventions:", error);
    }
  };

  const handleSubmitIncident = async () => {
    if (!incidentForm.patient_name || !incidentForm.fall_time || !incidentForm.description) {
      toast.error("Please fill in required fields: Patient Name, Time, and Description");
      return;
    }

    setLoading(true);
    try {
      const medsArray = medications.split(',').map(m => m.trim()).filter(m => m);
      
      const response = await api.post('/ehr/fall-automation/incident/create', {
        incident: incidentForm,
        patient_medications: medsArray
      });
      
      setAutomationResult(response.data);
      
      // Auto-select recommended DME
      const autoSelectDME = response.data.dme_recommendations
        .filter(d => d.auto_suggest)
        .map(d => d.name);
      setSelectedDME(autoSelectDME);
      
      toast.success("Fall incident recorded - Automation triggered!");
      
      if (response.data.md_alert.required) {
        toast.warning("MD Notification Required - See alerts panel", { duration: 5000 });
      }
      
      if (response.data.hope_gg_trigger) {
        toast.info("HOPE Section GG Reassessment Triggered", { duration: 5000 });
      }
      
      setShowDMEModal(true);
    } catch (error) {
      console.error("Failed to create incident:", error);
      toast.error(error.response?.data?.detail || "Failed to process fall incident");
    } finally {
      setLoading(false);
    }
  };

  const handleBeersCheck = async () => {
    if (!medications.trim()) {
      toast.error("Please enter medications to check");
      return;
    }

    setLoading(true);
    try {
      const medsArray = medications.split(',').map(m => m.trim()).filter(m => m);
      const response = await api.post('/ehr/fall-automation/beers-check', {
        patient_id: incidentForm.patient_id || 'demo',
        medications: medsArray
      });
      
      setBeersResult(response.data);
      
      if (response.data.md_alert_required) {
        toast.error("⚠️ HIGH FALL RISK - MD Alert Required", { duration: 5000 });
      } else if (response.data.high_risk_medications.length > 0) {
        toast.warning(`${response.data.high_risk_medications.length} fall-risk medications identified`);
      } else {
        toast.success("No high-risk medications identified");
      }
    } catch (error) {
      console.error("Beers check failed:", error);
      toast.error("Failed to check medications");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDME = async () => {
    if (selectedDME.length === 0) {
      toast.info("No DME items selected");
      setShowDMEModal(false);
      return;
    }

    setLoading(true);
    try {
      // Map display names back to keys
      const dmeKeys = selectedDME.map(name => {
        const rec = dmeRecommendations.find(r => r.name === name);
        return rec?.key || name.toLowerCase().replace(/ /g, '_');
      });

      await api.post('/ehr/fall-automation/dme/order', {
        patient_id: incidentForm.patient_id || 'demo',
        chart_id: incidentForm.chart_id || 'demo',
        incident_id: automationResult?.incident_id || 'demo',
        dme_items: dmeKeys,
        approved_by: user?.name || 'Staff',
        clinical_justification: `Fall incident on ${incidentForm.fall_date}`
      });
      
      toast.success(`${selectedDME.length} DME orders submitted!`);
      setShowDMEModal(false);
    } catch (error) {
      console.error("DME order failed:", error);
      toast.error("Failed to submit DME orders");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'major': return 'bg-red-100 text-red-700 border-red-300';
      case 'moderate': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'minor': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'no_injury': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'moderate': return 'bg-orange-100 text-orange-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                Fall Incident Automation
              </h1>
              <p className="text-muted-foreground mt-1">
                End-to-End automation: Incident → Assessment → Orders → Care Plan → IDG
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
              Back to EHR
            </Button>
          </div>
        </div>

        {/* Alert Banner if automation result exists */}
        {automationResult?.md_alert?.required && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <Bell className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <p className="font-semibold text-red-700">MD Notification Required</p>
              <p className="text-sm text-red-600">{automationResult.md_alert.message}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="destructive">
                  <Phone className="w-4 h-4 mr-1" /> Call MD Now
                </Button>
                <Button size="sm" variant="outline">Mark as Notified</Button>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="incident" data-testid="tab-incident">
              <Clipboard className="w-4 h-4 mr-2" />
              Fall Incident
            </TabsTrigger>
            <TabsTrigger value="beers" data-testid="tab-beers">
              <Pill className="w-4 h-4 mr-2" />
              Beers Criteria Check
            </TabsTrigger>
            <TabsTrigger value="automation" data-testid="tab-automation">
              <Zap className="w-4 h-4 mr-2" />
              Automation Results
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">
              <Shield className="w-4 h-4 mr-2" />
              Compliance Checklist
            </TabsTrigger>
          </TabsList>

          {/* Fall Incident Tab */}
          <TabsContent value="incident">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      Fall Incident Report
                    </CardTitle>
                    <CardDescription>
                      Document the fall - automations will trigger based on your entries
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Patient Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Patient Name *</Label>
                        <Input
                          value={incidentForm.patient_name}
                          onChange={(e) => setIncidentForm(prev => ({ ...prev, patient_name: e.target.value }))}
                          placeholder="Enter patient name"
                          data-testid="patient-name-input"
                        />
                      </div>
                      <div>
                        <Label>Patient ID</Label>
                        <Input
                          value={incidentForm.patient_id}
                          onChange={(e) => setIncidentForm(prev => ({ ...prev, patient_id: e.target.value }))}
                          placeholder="Patient ID"
                        />
                      </div>
                    </div>

                    {/* Date/Time/Location */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Fall Date *</Label>
                        <Input
                          type="date"
                          value={incidentForm.fall_date}
                          onChange={(e) => setIncidentForm(prev => ({ ...prev, fall_date: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Fall Time *</Label>
                        <Input
                          type="time"
                          value={incidentForm.fall_time}
                          onChange={(e) => setIncidentForm(prev => ({ ...prev, fall_time: e.target.value }))}
                          data-testid="fall-time-input"
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Select 
                          value={incidentForm.location}
                          onValueChange={(value) => setIncidentForm(prev => ({ ...prev, location: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bedroom">Bedroom</SelectItem>
                            <SelectItem value="bathroom">Bathroom</SelectItem>
                            <SelectItem value="living_room">Living Room</SelectItem>
                            <SelectItem value="kitchen">Kitchen</SelectItem>
                            <SelectItem value="hallway">Hallway</SelectItem>
                            <SelectItem value="outside">Outside</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Witnessed */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={incidentForm.witnessed}
                          onCheckedChange={(checked) => setIncidentForm(prev => ({ ...prev, witnessed: checked }))}
                        />
                        <Label>Witnessed Fall</Label>
                      </div>
                      {incidentForm.witnessed && (
                        <Input
                          value={incidentForm.witness_name}
                          onChange={(e) => setIncidentForm(prev => ({ ...prev, witness_name: e.target.value }))}
                          placeholder="Witness name"
                          className="flex-1"
                        />
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <Label>Fall Description *</Label>
                      <Textarea
                        value={incidentForm.description}
                        onChange={(e) => setIncidentForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe how the fall occurred..."
                        className="min-h-[100px]"
                        data-testid="fall-description-input"
                      />
                    </div>

                    {/* Injury Section */}
                    <div className="p-4 border rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2 mb-3">
                        <Switch
                          checked={incidentForm.injury_present}
                          onCheckedChange={(checked) => setIncidentForm(prev => ({ ...prev, injury_present: checked }))}
                        />
                        <Label className="font-semibold">Injury Present?</Label>
                      </div>
                      
                      {incidentForm.injury_present && (
                        <div className="space-y-3 pl-4 border-l-2 border-red-300">
                          <div>
                            <Label>Severity</Label>
                            <Select 
                              value={incidentForm.severity}
                              onValueChange={(value) => setIncidentForm(prev => ({ ...prev, severity: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="minor">Minor (Bruise, abrasion)</SelectItem>
                                <SelectItem value="moderate">Moderate (Laceration, sprain)</SelectItem>
                                <SelectItem value="major">Major (Fracture, head injury)</SelectItem>
                                <SelectItem value="unknown">Unknown - Needs Assessment</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Injury Description</Label>
                            <Textarea
                              value={incidentForm.injury_description}
                              onChange={(e) => setIncidentForm(prev => ({ ...prev, injury_description: e.target.value }))}
                              placeholder="Describe the injury..."
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Assessment Checks */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={incidentForm.vital_signs_checked}
                          onCheckedChange={(checked) => setIncidentForm(prev => ({ ...prev, vital_signs_checked: checked }))}
                        />
                        <Label>Vital Signs Checked</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={incidentForm.neuro_check_completed}
                          onCheckedChange={(checked) => setIncidentForm(prev => ({ ...prev, neuro_check_completed: checked }))}
                        />
                        <Label>Neuro Check Completed</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={incidentForm.md_notified}
                          onCheckedChange={(checked) => setIncidentForm(prev => ({ ...prev, md_notified: checked }))}
                        />
                        <Label>MD Notified</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={incidentForm.family_notified}
                          onCheckedChange={(checked) => setIncidentForm(prev => ({ ...prev, family_notified: checked }))}
                        />
                        <Label>Family Notified</Label>
                      </div>
                    </div>

                    {/* Medications for Beers Check */}
                    <div>
                      <Label>Current Medications (for Beers Criteria check)</Label>
                      <Textarea
                        value={medications}
                        onChange={(e) => setMedications(e.target.value)}
                        placeholder="Enter medications separated by commas (e.g., Alprazolam, Morphine, Quetiapine)"
                        className="min-h-[80px]"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The system will check for fall-risk medications and alert the MD
                      </p>
                    </div>

                    <Button 
                      onClick={handleSubmitIncident}
                      disabled={loading}
                      className="w-full bg-red-600 hover:bg-red-700"
                      data-testid="submit-incident-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Submit & Trigger Automations
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar - Quick Reference */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Automation Triggers</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-2 bg-red-50 rounded border border-red-200">
                      <p className="text-sm font-medium text-red-700">Major Injury → MD Alert (Immediate)</p>
                      <p className="text-xs text-red-600">+ HOPE GG Reassessment</p>
                    </div>
                    <div className="p-2 bg-orange-50 rounded border border-orange-200">
                      <p className="text-sm font-medium text-orange-700">Any Fall → Care Plan Update</p>
                      <p className="text-xs text-orange-600">Safety goal marked "Not Met"</p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm font-medium text-blue-700">Bathroom Fall → Grab Bars + Shower Chair</p>
                      <p className="text-xs text-blue-600">Auto-suggest DME</p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded border border-purple-200">
                      <p className="text-sm font-medium text-purple-700">2+ Falls in 30 Days → RCA Required</p>
                      <p className="text-xs text-purple-600">Quality Director notified</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">DME Options</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dmeRecommendations.slice(0, 5).map((dme, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span>{dme.name}</span>
                          <Badge variant="outline">${dme.estimated_cost}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Beers Criteria Tab */}
          <TabsContent value="beers">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="w-5 h-5 text-purple-600" />
                    Beers Criteria Fall Risk Checker
                  </CardTitle>
                  <CardDescription>
                    Screen medications against AGS Beers Criteria for fall risk
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Patient Medications (comma-separated)</Label>
                    <Textarea
                      value={medications}
                      onChange={(e) => setMedications(e.target.value)}
                      placeholder="e.g., Alprazolam 0.5mg, Morphine 15mg, Quetiapine 25mg, Diphenhydramine, Zolpidem"
                      className="min-h-[150px]"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleBeersCheck}
                    disabled={loading}
                    className="w-full"
                    data-testid="beers-check-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Activity className="w-4 h-4 mr-2" />
                    )}
                    Check Fall Risk Medications
                  </Button>

                  {/* Beers Result */}
                  {beersResult && (
                    <div className="mt-4 space-y-4">
                      <div className={`p-4 rounded-lg ${
                        beersResult.md_alert_required ? 'bg-red-50 border border-red-300' :
                        beersResult.high_risk_medications.length > 0 ? 'bg-orange-50 border border-orange-300' :
                        'bg-green-50 border border-green-300'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">Fall Risk Score</span>
                          <span className={`text-2xl font-bold ${
                            beersResult.total_fall_risk_score >= 60 ? 'text-red-600' :
                            beersResult.total_fall_risk_score >= 30 ? 'text-orange-600' :
                            'text-green-600'
                          }`}>
                            {beersResult.total_fall_risk_score}/100
                          </span>
                        </div>
                        {beersResult.md_alert_required && (
                          <Badge className="bg-red-100 text-red-700">⚠️ MD Alert Required</Badge>
                        )}
                      </div>

                      {beersResult.high_risk_medications.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-semibold">High-Risk Medications Identified</h4>
                          {beersResult.high_risk_medications.map((med, idx) => (
                            <div key={idx} className="p-3 border rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">{med.medication}</span>
                                <Badge className={getRiskLevelColor(med.risk_level)}>
                                  {med.risk_level.toUpperCase()} RISK
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">Category: {med.category}</p>
                              <p className="text-sm text-red-600 mt-1">{med.recommendation}</p>
                              <p className="text-sm text-green-600">Alternative: {med.alternative}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {beersResult.recommendations.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg">
                          <h4 className="font-semibold text-amber-700 mb-2">Recommendations</h4>
                          {beersResult.recommendations.map((rec, idx) => (
                            <p key={idx} className="text-sm text-amber-600 mb-1">• {rec}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Beers Reference Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Beers Criteria Reference</CardTitle>
                  <CardDescription>Fall-risk medication categories</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-red-700">Benzodiazepines</p>
                      <p className="text-xs text-muted-foreground">alprazolam, lorazepam, diazepam, clonazepam</p>
                      <Badge className="mt-1 bg-red-100 text-red-700">HIGH RISK</Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-red-700">Sedative Hypnotics</p>
                      <p className="text-xs text-muted-foreground">zolpidem, eszopiclone, zaleplon</p>
                      <Badge className="mt-1 bg-red-100 text-red-700">HIGH RISK</Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-red-700">First-Gen Antihistamines</p>
                      <p className="text-xs text-muted-foreground">diphenhydramine, chlorpheniramine</p>
                      <Badge className="mt-1 bg-red-100 text-red-700">HIGH RISK</Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-red-700">Anticholinergics</p>
                      <p className="text-xs text-muted-foreground">oxybutynin, tolterodine, promethazine</p>
                      <Badge className="mt-1 bg-red-100 text-red-700">HIGH RISK</Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-orange-700">Antipsychotics</p>
                      <p className="text-xs text-muted-foreground">quetiapine, risperidone, olanzapine</p>
                      <Badge className="mt-1 bg-orange-100 text-orange-700">MODERATE RISK</Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-orange-700">Opioids</p>
                      <p className="text-xs text-muted-foreground">morphine, oxycodone, fentanyl</p>
                      <Badge className="mt-1 bg-orange-100 text-orange-700">MODERATE RISK</Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-orange-700">Alpha Blockers</p>
                      <p className="text-xs text-muted-foreground">prazosin, terazosin, tamsulosin</p>
                      <Badge className="mt-1 bg-orange-100 text-orange-700">MODERATE RISK</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Automation Results Tab */}
          <TabsContent value="automation">
            {automationResult ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* DME Recommendations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-blue-600" />
                      Auto-Generated DME Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {automationResult.dme_recommendations.map((dme, idx) => (
                        <div key={idx} className={`p-3 border rounded-lg ${dme.auto_suggest ? 'bg-blue-50 border-blue-200' : ''}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{dme.name}</span>
                            <div className="flex items-center gap-2">
                              <Badge variant={dme.priority === 'high' ? 'destructive' : 'outline'}>
                                {dme.priority}
                              </Badge>
                              <Badge variant="outline">${dme.estimated_cost}</Badge>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{dme.description}</p>
                          {dme.auto_suggest && (
                            <Badge className="mt-2 bg-green-100 text-green-700">✓ Auto-Suggested</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button className="w-full mt-4" onClick={() => setShowDMEModal(true)}>
                      <ShoppingCart className="w-4 h-4 mr-2" /> Review & Approve DME Orders
                    </Button>
                  </CardContent>
                </Card>

                {/* Interventions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Stethoscope className="w-5 h-5 text-green-600" />
                      Triggered Interventions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {automationResult.interventions.map((intervention, idx) => (
                        <div key={idx} className={`p-3 border rounded-lg ${intervention.auto_order ? 'bg-green-50 border-green-200' : ''}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{intervention.description}</span>
                            <Badge variant={intervention.priority === 'high' ? 'destructive' : 'outline'}>
                              {intervention.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">Frequency: {intervention.frequency}</p>
                          {intervention.auto_order && (
                            <Badge className="mt-2 bg-green-100 text-green-700">✓ Auto-Ordered</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Care Plan Updates */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-purple-600" />
                      Care Plan Updates
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {automationResult.care_plan_updates.goals?.map((goal, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={goal.status === 'NOT_MET' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                              {goal.status}
                            </Badge>
                          </div>
                          <p className="font-medium">{goal.goal}</p>
                          <p className="text-sm text-red-600 mt-1">{goal.status_reason}</p>
                        </div>
                      ))}
                    </div>
                    {automationResult.care_plan_updates.requires_rn_signature && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm text-amber-700 font-medium">
                          <UserCheck className="w-4 h-4 inline mr-1" />
                          RN Signature Required
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* IDG & HOPE */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-indigo-600" />
                      IDG & Compliance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-indigo-50 rounded-lg">
                      <p className="font-medium text-indigo-700">IDG Agenda Priority</p>
                      <Badge className={`mt-1 ${
                        automationResult.idg_priority === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                        automationResult.idg_priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {automationResult.idg_priority}
                      </Badge>
                      <p className="text-sm text-indigo-600 mt-1">
                        Patient will appear in "Safety/Acute Events" category
                      </p>
                    </div>

                    {automationResult.hope_gg_trigger && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="font-medium text-purple-700">
                          <Activity className="w-4 h-4 inline mr-1" />
                          HOPE Section GG Reassessment Required
                        </p>
                        <p className="text-sm text-purple-600 mt-1">
                          Functional status may have changed after fall
                        </p>
                        <Button size="sm" variant="outline" className="mt-2">
                          Start GG Assessment
                        </Button>
                      </div>
                    )}

                    {automationResult.beers_alerts?.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="font-medium text-red-700">
                          <Pill className="w-4 h-4 inline mr-1" />
                          Beers Criteria Alerts ({automationResult.beers_alerts.length})
                        </p>
                        {automationResult.beers_alerts.map((alert, idx) => (
                          <p key={idx} className="text-sm text-red-600 mt-1">
                            • {alert.medication}: {alert.recommendation}
                          </p>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">
                    Submit a fall incident to see automation results
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Compliance Checklist Tab */}
          <TabsContent value="compliance">
            {automationResult ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Fall Incident Compliance Checklist
                  </CardTitle>
                  <CardDescription>
                    Track completion of required documentation and interventions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {automationResult.compliance_checklist.map((item, idx) => (
                      <div key={idx} className={`p-3 border rounded-lg flex items-center justify-between ${
                        item.status === 'complete' ? 'bg-green-50 border-green-200' :
                        item.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                        'bg-gray-50'
                      }`}>
                        <div className="flex items-center gap-3">
                          {item.status === 'complete' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : item.status === 'pending' ? (
                            <Clock className="w-5 h-5 text-amber-600" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                          )}
                          <span className="font-medium">{item.item}</span>
                        </div>
                        <Badge className={
                          item.status === 'complete' ? 'bg-green-100 text-green-700' :
                          item.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {item.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <p className="font-semibold text-blue-700 mb-2">Survey Readiness Status</p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="h-3 bg-blue-200 rounded-full">
                          <div 
                            className="h-3 bg-blue-600 rounded-full"
                            style={{ 
                              width: `${(automationResult.compliance_checklist.filter(c => c.status === 'complete').length / automationResult.compliance_checklist.length) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                      <span className="font-bold text-blue-700">
                        {Math.round((automationResult.compliance_checklist.filter(c => c.status === 'complete').length / automationResult.compliance_checklist.length) * 100)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">
                    Submit a fall incident to see compliance checklist
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* DME Order Modal */}
      <Dialog open={showDMEModal} onOpenChange={setShowDMEModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Approve DME Orders
            </DialogTitle>
            <DialogDescription>
              Select equipment to order for fall prevention
            </DialogDescription>
          </DialogHeader>
          
          {automationResult && (
            <div className="space-y-3">
              {automationResult.dme_recommendations.map((dme, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                  <Checkbox 
                    checked={selectedDME.includes(dme.name)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedDME(prev => [...prev, dme.name]);
                      } else {
                        setSelectedDME(prev => prev.filter(n => n !== dme.name));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{dme.name}</span>
                      <Badge variant="outline">${dme.estimated_cost}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{dme.description}</p>
                    <p className="text-xs text-green-600 mt-1">
                      <Shield className="w-3 h-3 inline mr-1" />
                      {dme.justification}
                    </p>
                    {dme.medicare_covered && (
                      <Badge className="mt-1 bg-blue-100 text-blue-700">Medicare Covered</Badge>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between">
                  <span>Selected Items:</span>
                  <span className="font-bold">{selectedDME.length}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Estimated Total:</span>
                  <span className="font-bold text-blue-700">
                    ${automationResult.dme_recommendations
                      .filter(d => selectedDME.includes(d.name))
                      .reduce((sum, d) => sum + d.estimated_cost, 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDMEModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleApproveDME} disabled={loading}>
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Approve & Order ({selectedDME.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Add missing import for ShoppingCart
const ShoppingCart = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

export default FallAutomationPage;
